import React, { useContext, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { useCall } from '../../context/CallContext';
import { SocketContext } from '../../context/socket';
import { AuthContext } from '../../context/AuthContext';

function GlobalCallModal({ location }) {
  const { socket } = useContext(SocketContext);
  const { player } = useContext(AuthContext);
  const { incoming, active, acceptCall, declineCall, setMinimized } = useCall();

  // Show whenever there is an incoming call and no active call
  const show = Boolean(incoming && !active);
  const payload = useMemo(() => incoming || {}, [incoming]);

  // Who am I?
  const meId = player?.data?.player?._id;
  const fromUserId = payload?.fromUserId || payload?.from;
  const isSelf = meId && fromUserId && String(meId) === String(fromUserId);

  // Pretty label for caller + video/audio detection
  const meta = useMemo(() => {
    const {
      fromPlayerId,
      fromName,
      from,
      fromUserId: fUid,
      fromProfilePhoto,
      isVideo, // ← source of truth from CallContext.incoming
      convoId,
      conversationId,
    } = payload;

    const name = fromPlayerId
      ? `@${fromPlayerId}`
      : fromName || from || fUid || 'Incoming call';

    // 🔒 Only treat as video when incoming.isVideo === true
    const video = isVideo === true;

    const avatar =
      fromProfilePhoto ||
      'https://res.cloudinary.com/joegalaxy/image/upload/v1648159989/Images/avocado2_j5hhmc.png';

    return {
      name,
      isVideo: video,
      avatar,
      convoId: convoId || conversationId || null,
    };
  }, [payload]);

  // Debug log
  useEffect(() => {
    // Only run when modal becomes visible
    if (!show) return;

    // 🔥 1. Clean incoming event log
    console.log('[GlobalCallModal] incoming →', {
      fromUserId,
      fromPlayerId: payload?.fromPlayerId,
      fromName: payload?.fromName,
      isVideo: meta.isVideo,
      convoId: meta.convoId,
      raw: payload,
    });

    // 🔥 2. Expose incoming payload + meta to window for nuclear debugging
    window.__jg = window.__jg || {};
    window.__jg.callModal = {
      incomingPayload: payload,
      meta,
      fromUserId,
      ts: Date.now(),
    };

    // 🔥 3. Additional debug only in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[GlobalCallModal DEBUG]', {
        incoming,
        active,
        ts: new Date().toISOString(),
      });
    }
  }, [show, payload, meta, fromUserId]);

  // Join conversation room for this call (for real-time updates)
  useEffect(() => {
    if (!show || !payload) return;
    const convoId = payload.convoId || payload.conversationId;
    if (socket && convoId) {
      socket.emit('joinConversationRoom', convoId);
      console.log('[GlobalCallModal] joined conversation room:', convoId);
    }
  }, [show, payload, socket]);

  // ❗️IMPORTANT: do NOT hide on /messenger anymore
  // If it's a self-initiated outgoing call, don't show this modal.
  if (!show || isSelf) return null;

  const handleAccept = () => {
    try {
      setMinimized(false);
    } catch (e) {}
    console.log('[GlobalCallModal] accept clicked');
    acceptCall();
  };

  const handleDecline = () => {
    try {
      setMinimized(false);
    } catch (e) {}
    console.log('[GlobalCallModal] decline clicked');
    declineCall();
  };

  return (
    <div className="jg-global-call-modal-root">
      <div className="call-modal-backdrop" />
      <div className="call-modal-shell">
        <div className="call-modal-content">
          <div className="header-row">
            <div className="pill">
              Incoming {meta.isVideo ? 'Video' : 'Audio'} Call
            </div>
          </div>

          <div className="body-row">
            <div className="avatar-wrap">
              <img
                src={meta.avatar}
                alt={meta.name}
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
            <div className="text-wrap">
              <div className="caller-name">{meta.name}</div>
              <div className="subcopy">
                {meta.isVideo
                  ? 'wants to video chat with you'
                  : 'is calling you'}
              </div>
            </div>
          </div>

          <div className="button-row">
            <button className="btn decline" onClick={handleDecline}>
              Decline
            </button>
            <button className="btn accept" onClick={handleAccept}>
              Accept
            </button>
          </div>
        </div>
      </div>

      {/* NOTE: plain <style>, no jsx prop */}
      <style>{`
        .jg-global-call-modal-root {
          position: fixed;
          inset: 0;
          z-index: 2147483646;
          pointer-events: none;
        }

        .call-modal-backdrop {
          position: fixed;
          inset: 0;
          background: radial-gradient(circle at top, rgba(0,0,0,0.65), rgba(0,0,0,0.9));
          backdrop-filter: blur(3px);
          -webkit-backdrop-filter: blur(3px);
          pointer-events: auto;
        }

        .call-modal-shell {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
          pointer-events: none;
        }

        .call-modal-content {
          pointer-events: auto;
          width: min(420px, 94vw);
          background: #0d1117;
          color: #fff;
          border-radius: 18px;
          box-shadow: 0 18px 45px rgba(0,0,0,0.75);
          border: 1px solid rgba(255,255,255,0.12);
          padding: 20px 18px 16px;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          max-height: min(420px, 90vh);
          overflow-y: auto;
        }

        .header-row {
          display: flex;
          justify-content: center;
          margin-bottom: 14px;
        }

        .pill {
          padding: 4px 10px;
          border-radius: 999px;
          background: linear-gradient(135deg,#22c55e,#16a34a);
          font-size: 11px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          font-weight: 700;
        }

        .body-row {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .avatar-wrap {
          width: 60px;
          height: 60px;
          border-radius: 999px;
          overflow: hidden;
          border: 2px solid rgba(34,197,94,0.75);
          box-shadow: 0 0 0 4px rgba(34,197,94,0.25);
          flex-shrink: 0;
        }

        .avatar-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .text-wrap {
          flex: 1;
          min-width: 0;
        }

        .caller-name {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .subcopy {
          font-size: 13px;
          opacity: 0.78;
        }

        .button-row {
          display: flex;
          gap: 10px;
          margin-top: 18px;
        }

        .btn {
          flex: 1;
          min-width: 0;
          padding: 10px 14px;
          border-radius: 999px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          border: none;
          transition: transform 0.08s ease, box-shadow 0.08s ease, background 0.08s ease;
        }

        .btn:active {
          transform: scale(0.97);
          box-shadow: 0 0 0 0 rgba(0,0,0,0.4);
        }

        .btn.accept {
          background: linear-gradient(135deg,#22c55e,#16a34a);
          color: #fff;
          box-shadow: 0 10px 24px rgba(22,163,74,0.55);
        }

        .btn.decline {
          background: #111827;
          color: #fca5a5;
          border: 1px solid rgba(248,113,113,0.45);
        }

        @media (max-width: 480px) {
          .call-modal-shell {
            align-items: center;
            justify-content: center;
            padding: 10px;
          }
          .call-modal-content {
            width: 94vw;
            border-radius: 16px;
            padding: 16px 14px 12px;
            max-height: 90vh;
          }
          .button-row {
            flex-direction: column-reverse;
          }
        }
      `}</style>
    </div>
  );
}

export default withRouter(GlobalCallModal);
